import { Address, FullCity } from "@deligoo/shared"
import {
  CityField,
  fetchFindDitanceBetweens,
  fetchFindPostalCode,
  Field,
  Input,
  Select,
  StreetField,
} from "@deligoo/ui"
import { debounce } from "lodash"
import { useEffect, useMemo, useRef } from "react"
import { Controller, useFormContext, useFormState, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import NumberFormat from "react-number-format"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { DistanceBar } from "@/components/DistanceBar"
import { selectClientById } from "@/store/clients"
import { selectOrderById } from "@/store/orders"
import { selectCurrentTeamId } from "@/store/teams"
import { Order } from "@/types"
import { ORDER_ALLOWED_STATES } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import cls from "../EditOrderPanel.module.scss"
import { StateSelect } from "./StateSelect"

export const Delivery = ({ orderId }: { orderId: Order["id"] }) => {
  const currentTeamId = useAppSelector(selectCurrentTeamId)

  const order = useAppSelector(selectOrderById(orderId))
  const client = useAppSelector(selectClientById(order?.client_id || 0))

  const { watch, setValue, register } = useFormContext()

  const { errors } = useFormState()

  const distances = watch(["distance_for_car", "distance_for_bicycle", "distance_for_scooter"])

  const currentMaxDistance = Math.max(...distances)

  const deliveryAttributes = useWatch({
    name: "delivery_attributes",
    defaultValue: order?.delivery || {},
  })

  const address: Address = useMemo(
    () => ({
      city: deliveryAttributes.city || "",
      state: deliveryAttributes.city_state || "",
      county: deliveryAttributes.county || "",
      municipality: deliveryAttributes.municipality || "",
      street: deliveryAttributes.street || "",
      house_number: deliveryAttributes.house_number || "",
      postal_code: deliveryAttributes.postal_code || "",
    }),
    [deliveryAttributes]
  )

  const { t } = useTranslation()

  async function updatePostalCode({ postal_code, ...partialAddress }: Address) {
    const isValid = Object.values(partialAddress).every(Boolean)

    if (!isValid || postal_code) {
      debouncedUpdatePostalCode.cancel()
      return
    }

    const { response, json } = await fetchFindPostalCode(partialAddress)

    if (json.errors) {
      handleStandardError(response, json)
      return
    }

    if (response.ok && json.data) {
      setValue("delivery_attributes.postal_code", json.data.postal_code)
    }
  }

  const debouncedUpdatePostalCode = useRef(debounce(updatePostalCode, 1000)).current

  useEffect(() => {
    debouncedUpdatePostalCode(address)

    return () => {
      debouncedUpdatePostalCode.cancel()
    }
  }, [address, debouncedUpdatePostalCode])

  useEffect(() => {
    updateDistances()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.postal_code])

  async function updateDistances() {
    if (!currentTeamId || !order?.pickup) return

    const pickup = {
      city: order.pickup.city,
      street: order.pickup.street,
      house_number: order.pickup.house_number,
      postal_code: order.pickup.postal_code,
    }

    const delivery = {
      city: address.city,
      street: address.street,
      house_number: address.house_number,
      postal_code: address.postal_code,
    }

    if (!Object.values(pickup).every(Boolean) || !Object.values(delivery).every(Boolean)) return

    const { response, json } = await fetchFindDitanceBetweens({ teamId: currentTeamId, pickup, delivery })

    setValue("distance_for_car", json.data?.distance_for_car || undefined)
    setValue("distance_for_bicycle", json.data?.distance_for_bicycle || undefined)
    setValue("distance_for_scooter", json.data?.distance_for_scooter || undefined)

    if (!response.ok && json.errors) {
      handleStandardError(response, json.errors)
    }
  }

  if (!order) return null

  return (
    <>
      {order.delivery && (
        <Card.Section>
          <div className="h300 font-weight-bold mb-8">Doręczenie</div>

          <div className={cls.deliveryForm}>
            <input
              {...register("delivery_attributes.city", { required: true })}
              defaultValue={order.delivery.city}
              type="hidden"
            />

            <input
              {...register("delivery_attributes.city_state", { required: true })}
              defaultValue={order.delivery.city_state}
              type="hidden"
            />

            <input
              {...register("delivery_attributes.county", { required: true })}
              defaultValue={order.delivery.county}
              type="hidden"
            />

            <input
              {...register("delivery_attributes.municipality", { required: true })}
              defaultValue={order.delivery.municipality}
              type="hidden"
            />

            <Field name="phone" label="Telefon" error={errors.phone?.message} className={cls.lg} required>
              <Controller
                name="delivery_attributes.phone"
                rules={{
                  required: "Podaj telefon",
                  pattern: {
                    value: /[0-9]{3} [0-9]{3} [0-9]{3}/,
                    message: "Podaj poprawny numer telefonu",
                  },
                }}
                defaultValue={order.delivery.phone}
                render={({ field }) => (
                  <NumberFormat {...field} id="phone" format="### ### ###" customInput={Input} selectOnFocus plain />
                )}
              />
            </Field>

            <CityField
              error={
                (errors?.city || errors?.state || errors?.county || errors?.municipality) && "Wybierz miasto z listy"
              }
              value={address.city}
              setValue={(value: FullCity) => {
                setValue("delivery_attributes.city", value.city)
                setValue("delivery_attributes.city_state", value.state)
                setValue("delivery_attributes.county", value.county)
                setValue("delivery_attributes.municipality", value.municipality)
                setValue("delivery_attributes.postal_code", "")
              }}
              className={cls.md}
              inputProps={{ size: "medium" }}
            />

            <StreetField
              error={errors.street?.message}
              value={address.street}
              setValue={(value: string) => {
                setValue("delivery_attributes.street", value)
                setValue("delivery_attributes.postal_code", "")
              }}
              cityValue={{
                city: address.city,
                state: address.state,
                county: address.county,
                municipality: address.municipality,
              }}
              className={cls.md}
              inputProps={{ size: "medium", ...register("delivery_attributes.street", { required: "Podaj ulicę" }) }}
            />

            <Field
              error={errors.house_number?.message}
              name="house_number"
              className={cls.sm}
              label="Nr domu"
              inputProps={{
                size: "medium",
                defaultValue: order.delivery.house_number,
                ...register("delivery_attributes.house_number", {
                  required: "Podaj nr domu",
                  onBlur: () => setValue("delivery_attributes.postal_code", ""),
                }),
              }}
              required
            />

            <Field
              name="flat_number"
              className={cls.sm}
              label="Nr miesz."
              inputProps={{
                size: "medium",
                defaultValue: order.delivery.flat_number,
                ...register("delivery_attributes.flat_number"),
              }}
            />

            <Field
              name="postal_code"
              className={cls.sm}
              label="Kod pocztowy"
              error={errors.postal_code?.message}
              inputProps={{ size: "medium" }}
              required
            >
              <Controller
                name="delivery_attributes.postal_code"
                rules={{
                  required: "Podaj kod pocztowy",
                  pattern: {
                    value: /[0-9]{2}-[0-9]{3}/,
                    message: "Podaj poprawny kod pocztowy",
                  },
                }}
                defaultValue={order.delivery.postal_code}
                render={({ field }) => (
                  <NumberFormat {...field} id="postal_code" format="##-###" customInput={Input} selectOnFocus plain />
                )}
              />
            </Field>

            <Field
              name="floor"
              className={cls.sm}
              label="Piętro"
              inputProps={{
                type: "number",
                size: "medium",
                defaultValue: order.delivery.floor,
                ...register("delivery_attributes.floor"),
              }}
            />

            <Field
              name="staircase"
              className={cls.sm}
              label="Klatka"
              inputProps={{
                size: "medium",
                defaultValue: order.delivery.staircase,
                ...register("delivery_attributes.staircase"),
              }}
            />

            <Field
              name="access_code"
              className={cls.sm}
              label="Kod dostępu"
              inputProps={{
                size: "medium",
                defaultValue: order.delivery.access_code,
                ...register("delivery_attributes.access_code"),
              }}
            />
          </div>

          <StateSelect name="delivery_attributes.state" defaultValue={order.delivery.state} />
        </Card.Section>
      )}

      <Card.Section>
        <Field name="client_comment" label="Uwagi do zamówienia otrzymane od klienta">
          <textarea
            id="client_comment"
            placeholder="Treść uwag przekazanych przez klienta"
            defaultValue={order.client_comment || undefined}
            {...register("client_comment_attributes.description")}
          />
        </Field>
      </Card.Section>

      <Card.Section>
        <Field name="state" label="Status zlecenia" error={errors.state?.message}>
          <Select id="state" defaultValue={order.state} {...register("state", { required: "Podaj status zlecenia" })}>
            {ORDER_ALLOWED_STATES.map((state) => (
              <option value={state} key={`order-${state}`}>
                {t(`orders:${state}`)}
              </option>
            ))}
          </Select>
        </Field>
      </Card.Section>

      {order.pickup && order.delivery && (
        <Card.Section>
          <div className="h300">Odległość między adresem odbioru a adresem doręczenia (km)</div>

          <DistanceBar distance={currentMaxDistance} limit={client?.deligoo_max_distance_limit}>
            <div className={cls.distanceInputs}>
              <Field
                label="Samochód"
                error={errors.distance_for_car?.message}
                inputProps={{
                  ...register("distance_for_car", { required: "Podaj wartość" }),
                  size: "medium",
                  type: "number",
                  step: 0.01,
                  min: 0,
                }}
              />

              <Field
                label="Rower"
                error={errors.distance_for_bicycle?.message}
                inputProps={{
                  ...register("distance_for_bicycle", { required: "Podaj wartość" }),
                  size: "medium",
                  type: "number",
                  step: 0.01,
                  min: 0,
                }}
              />

              <Field
                label="Skuter"
                error={errors.distance_for_scooter?.message}
                inputProps={{
                  ...register("distance_for_scooter", { required: "Podaj wartość" }),
                  size: "medium",
                  type: "number",
                  step: 0.01,
                  min: 0,
                }}
              />
            </div>
          </DistanceBar>
        </Card.Section>
      )}
    </>
  )
}
